import { Component } from '@theme/component';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, FavoritesChangedEvent, FavoriteAddedEvent, FavoriteRemovedEvent } from '@theme/events';
import { Favorites } from '@theme/favorites';

/**
 * A button that toggles a product's favorited state, stored in local storage.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} control - The clickable button.
 *
 * @extends {Component<Refs>}
 */
class FavoriteButton extends Component {
  requiredRefs = ['control'];

  connectedCallback() {
    super.connectedCallback();

    this.#render(Favorites.has(this.productHandle));
    document.addEventListener(ThemeEvents.favoritesChanged, this.#onFavoritesChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.favoritesChanged, this.#onFavoritesChanged);
  }

  /** @returns {string} The product handle this button favorites. */
  get productHandle() {
    return this.dataset.productHandle ?? '';
  }

  /**
   * Toggles the favorited state and plays the burst animation when favoriting.
   * @param {Event} event - The click event.
   */
  toggle = async (event) => {
    event.preventDefault();

    if (!this.productHandle) return;

    const previousIndex = Favorites.getAll().indexOf(this.productHandle);
    const isFavorite = Favorites.toggle(this.productHandle);

    this.#render(isFavorite);
    this.classList.add(isFavorite ? 'is-animating' : 'is-removing');

    if (isFavorite) {
      document.dispatchEvent(
        new FavoriteAddedEvent({
          handle: this.productHandle,
          title: this.dataset.productTitle ?? '',
          image: this.dataset.productImage ?? '',
        })
      );
    } else {
      document.dispatchEvent(
        new FavoriteRemovedEvent({
          handle: this.productHandle,
          title: this.dataset.productTitle ?? '',
          image: this.dataset.productImage ?? '',
          index: previousIndex,
        })
      );
    }

    // Yield first so the browser sees the class change before we look for animations to wait on.
    await yieldToMainThread();
    await onAnimationEnd(this);

    this.classList.remove('is-animating', 'is-removing');
  };

  /**
   * Handles favorited state changes triggered by another instance of this button for the same product.
   * @param {Event} event - The favorites changed event.
   */
  #onFavoritesChanged = (event) => {
    if (!(event instanceof FavoritesChangedEvent)) return;
    if (event.detail.handle !== this.productHandle) return;

    this.#render(event.detail.isFavorite);
  };

  /**
   * @param {boolean} isFavorite - Whether the product is currently favorited.
   */
  #render(isFavorite) {
    this.classList.toggle('is-favorited', isFavorite);
    this.refs.control.setAttribute('aria-pressed', String(isFavorite));
    this.refs.control.setAttribute(
      'aria-label',
      isFavorite ? this.dataset.labelRemove ?? '' : this.dataset.labelAdd ?? ''
    );
  }
}

if (!customElements.get('favorite-button')) {
  customElements.define('favorite-button', FavoriteButton);
}
